import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  useCommandChangeConfiguration,
  useCommandDataTransfer,
  useCommandReset,
  useCommandUpdateFirmware,
} from '../../../../app/ApiGen';
import { Section, useWizard, Workspace } from '../../../../design-system';
import { parseOptionalInt } from '../../../../utils/firmware';
import { ChargePointState, CommandState, CommandsWizardState, CommandType } from '../types';

// Pause between sequential dispatches to avoid tripping gateway rate limits on large selections.
const STAGGER_MS = 300;

type ItemStatus = 'pending' | 'loading' | 'success' | 'error';
type ItemResult = { status: ItemStatus; message?: string };

const getCommandTitle = (commandType: CommandType) => {
  switch (commandType) {
    case CommandType.UPDATE_FIRMWARE:
      return 'Update firmware';
    case CommandType.RESET:
      return 'Reset';
    case CommandType.CHANGE_CONFIGURATION:
      return 'Change configuration';
    case CommandType.DATA_TRANSFER:
      return 'Data transfer';
    default:
      return '';
  }
};

const errorMessage = (err: unknown): string => {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'Failed';
};

const StatusCell: React.FC<{ result: ItemResult; onRetry: () => void }> = ({ result, onRetry }) => {
  switch (result.status) {
    case 'loading':
      return <CircularProgress size={20} />;
    case 'success':
      return (
        <Typography aria-label={`Status: ${result.message ?? 'Success'}`}>{result.message ?? 'Accepted'}</Typography>
      );
    case 'error':
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="caption" color="error" aria-label="Status: Error">
            {result.message}
          </Typography>
          <ErrorOutlineIcon color="error" fontSize="small" />
          <Tooltip title="Retry this charge point">
            <IconButton size="small" onClick={onRetry} aria-label="Retry">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    default:
      return (
        <Typography variant="caption" color="textSecondary">
          Pending
        </Typography>
      );
  }
};

const ChargePointRow: React.FC<{
  chargePoint: ChargePointState;
  result: ItemResult;
  onRetry: () => void;
}> = ({ chargePoint, result, onRetry }) => (
  <ListItem style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
    <div>
      <Typography variant="body1">{chargePoint.name ?? chargePoint.ocppChargePointId}</Typography>
      <Typography variant="caption" color="textSecondary">
        {[chargePoint.ocppChargePointId, chargePoint.serial].filter(Boolean).join(' | ')}
      </Typography>
    </div>
    <StatusCell result={result} onRetry={onRetry} />
  </ListItem>
);

type ExecutorProps = {
  chargePoint: ChargePointState;
  command: CommandState['command'];
  onComplete: (result: ItemResult) => void;
};

// Mounted one at a time by the parent. Holds the per-charge-point mutate hook and fires once
// on mount; reports success or failure back via onComplete. Unmounted when the parent advances.
const Executor: React.FC<ExecutorProps> = ({ chargePoint, command, onComplete }) => {
  const { id } = chargePoint;
  const firmware = useCommandUpdateFirmware({ id });
  const reset = useCommandReset({ id });
  const changeConfig = useCommandChangeConfiguration({ id });
  const dataTransfer = useCommandDataTransfer({ id });

  const callbackRef = React.useRef(onComplete);
  callbackRef.current = onComplete;

  React.useEffect(() => {
    const readStatus = (response: unknown): string => {
      const candidate = (response as { data?: { status?: unknown } } | undefined)?.data?.status;
      return typeof candidate === 'string' ? candidate : 'Accepted';
    };

    const run = async (): Promise<ItemResult> => {
      switch (command.type) {
        case CommandType.UPDATE_FIRMWARE: {
          const response = await firmware.mutate({
            location: command.firmwareUrl ?? '',
            retries: parseOptionalInt(command.retries),
            retryInterval: parseOptionalInt(command.retryInterval),
          });
          return { status: 'success', message: readStatus(response) };
        }
        case CommandType.RESET: {
          const response = await reset.mutate({ resetType: command.resetType ?? 'Soft' });
          return { status: 'success', message: readStatus(response) };
        }
        case CommandType.CHANGE_CONFIGURATION: {
          const response = await changeConfig.mutate({
            key: command.configurationKey ?? '',
            value: command.configurationValue ?? '',
          });
          return { status: 'success', message: readStatus(response) };
        }
        case CommandType.DATA_TRANSFER: {
          const response = await dataTransfer.mutate({
            vendorId: 'Evnex',
            messageId: command.messageId,
            data: '',
          });
          return { status: 'success', message: readStatus(response) };
        }
        default:
          return { status: 'error', message: 'Unsupported command' };
      }
    };

    run()
      .then((result) => callbackRef.current(result))
      .catch((e) => {
        console.error('Error sending command:', e);
        callbackRef.current({ status: 'error', message: errorMessage(e) });
      });
    // Fire once per mount — parent remounts (via key) to re-run for retries.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export const ResultsStep: React.FC = () => {
  const { state: untypedState, prevStep, nextStep } = useWizard();
  const { command } = untypedState as CommandsWizardState;

  const {
    state: { chargePoints },
  } = useLocation<Pick<CommandsWizardState, 'chargePoints'>>();

  const [results, setResults] = React.useState<Map<string, ItemResult>>(
    () => new Map(chargePoints.map((cp) => [cp.id, { status: 'pending' as const }])),
  );
  // Each queue entry carries the charge point id plus a unique key used as the React key on the
  // Executor, so retrying the same id produces a fresh mount rather than reconciling with the
  // previous (already-resolved) Executor.
  type QueueEntry = { id: string; key: string };
  const nextQueueKey = React.useRef(0);
  const makeQueueEntry = React.useCallback((id: string): QueueEntry => {
    nextQueueKey.current += 1;
    return { id, key: `${id}-${nextQueueKey.current}` };
  }, []);
  const [queue, setQueue] = React.useState<QueueEntry[]>(() => chargePoints.map((cp) => makeQueueEntry(cp.id)));
  // How many queue positions have been dispatched so far. Dispatches advance on a stagger tick
  // independently of whether earlier ones have completed — `startedCount` is always ahead of
  // (or equal to) the number of resolved rows.
  const [startedCount, setStartedCount] = React.useState(0);

  // Kick off each queue position on its own 300ms tick. The first one fires on the next tick;
  // after that, one per STAGGER_MS. Cleanup cancels the pending tick on unmount or when queue
  // grows (retries) so we don't schedule duplicates. When the tick fires we flip the entry to
  // 'loading' and advance startedCount in the same update — doing it in a separate effect that
  // re-ran on queue changes caused retries to spuriously flash the previous last row.
  React.useEffect(() => {
    if (startedCount >= queue.length) return undefined;
    const delay = startedCount === 0 ? 0 : STAGGER_MS;
    const timer = setTimeout(() => {
      const entry = queue[startedCount];
      if (entry) {
        setResults((prev) => new Map(prev).set(entry.id, { status: 'loading' }));
      }
      setStartedCount((c) => c + 1);
    }, delay);
    return () => clearTimeout(timer);
  }, [startedCount, queue]);

  const handleComplete = React.useCallback((id: string, result: ItemResult) => {
    setResults((prev) => new Map(prev).set(id, result));
  }, []);

  const retryOne = React.useCallback(
    (id: string) => {
      setResults((prev) => new Map(prev).set(id, { status: 'pending' }));
      setQueue((prev) => [...prev, makeQueueEntry(id)]);
    },
    [makeQueueEntry],
  );

  const retryAllFailed = React.useCallback(() => {
    const failedIds = chargePoints.filter((cp) => results.get(cp.id)?.status === 'error').map((cp) => cp.id);
    if (failedIds.length === 0) return;
    setResults((prev) => {
      const next = new Map(prev);
      failedIds.forEach((id) => {
        next.set(id, { status: 'pending' });
      });
      return next;
    });
    setQueue((prev) => [...prev, ...failedIds.map(makeQueueEntry)]);
  }, [chargePoints, makeQueueEntry, results]);

  const resultValues = Array.from(results.values());
  const failedCount = resultValues.filter((r) => r.status === 'error').length;
  const inFlight = resultValues.some((r) => r.status === 'pending' || r.status === 'loading');
  const allDone = startedCount >= queue.length && !inFlight;

  return (
    <Workspace maxWidth="lg">
      <Section title={`${getCommandTitle(command.type)} results`}>
        <List>
          {chargePoints.map((cp) => {
            const result = results.get(cp.id) ?? { status: 'pending' as const };
            return <ChargePointRow key={cp.id} chargePoint={cp} result={result} onRetry={() => retryOne(cp.id)} />;
          })}
        </List>
        {queue.slice(0, startedCount).map((entry) => {
          const chargePoint = chargePoints.find((cp) => cp.id === entry.id);
          if (!chargePoint) return null;
          return (
            <Executor
              key={entry.key}
              chargePoint={chargePoint}
              command={command}
              onComplete={(result) => handleComplete(entry.id, result)}
            />
          );
        })}
        <Box pt={2} display="flex" gap={1}>
          {(!allDone || failedCount > 0) && (
            <Button color="inherit" onClick={prevStep} variant="contained">
              Back
            </Button>
          )}
          {allDone && failedCount > 0 && (
            <Button color="primary" variant="outlined" onClick={retryAllFailed}>
              Retry {failedCount} failed
            </Button>
          )}
          {allDone && (
            <Button color="primary" variant="contained" onClick={nextStep} sx={{ ml: 'auto' }}>
              Done
            </Button>
          )}
        </Box>
      </Section>
    </Workspace>
  );
};
